import type { Dispatch } from "@reduxjs/toolkit";

import {
  setFocusedViewTiles,
  setFocusedVisualizationTile,
  setGameTiles,
  setHittingTiles,
  setPitchingTiles,
  setSingleViewTile,
  setVisualizationTiles,
} from "../store/slices/tiles";
import type {
  IFocusedTileGroup,
  IFocusedVisualizationTile,
  ISingleViewTile,
  ITileConfig,
  ITileGroup,
  IVisualizationTileGroup,
} from "../store/types";

export type TileTypeKey =
  | "hitting"
  | "pitching"
  | "visualization"
  | "focused"
  | "focusedVisualization"
  | "single"
  | "game";

const TILE_ACTION_MAP = {
  hitting: setHittingTiles,
  pitching: setPitchingTiles,
  visualization: setVisualizationTiles,
  focused: setFocusedViewTiles,
  focusedVisualization: setFocusedVisualizationTile,
  single: setSingleViewTile,
  game: setGameTiles,
} as const;

export interface TilesState {
  gameTiles: ITileGroup;
  hittingTiles: ITileGroup;
  pitchingTiles: ITileGroup;
  visualizationTiles: IVisualizationTileGroup;
  focusedViewTiles: IFocusedTileGroup;
  focusedVisualizationTile: IFocusedVisualizationTile;
  singleViewTile: ISingleViewTile;
}

const TILE_STATE_MAP = {
  hitting: (tilesState: TilesState) => tilesState.hittingTiles,
  pitching: (tilesState: TilesState) => tilesState.pitchingTiles,
  visualization: (tilesState: TilesState) => tilesState.visualizationTiles,
  focused: (tilesState: TilesState) => tilesState.focusedViewTiles,
  focusedVisualization: (tilesState: TilesState) =>
    tilesState.focusedVisualizationTile,
  single: (tilesState: TilesState) => tilesState.singleViewTile,
  game: (tilesState: TilesState) => tilesState.gameTiles,
} as const;

const isValidTileType = (tileType: string): tileType is TileTypeKey => {
  return tileType in TILE_ACTION_MAP;
};

export const updateTile = (
  tileType: TileTypeKey,
  tileKey: string,
  newTileConfig: ITileConfig,
  tilesState: TilesState,
  dispatch: Dispatch,
) => {
  if (!isValidTileType(tileType)) {
    console.warn(`Invalid tile type: ${tileType}`);
    return;
  }

  const action = TILE_ACTION_MAP[tileType];
  const currentTiles = TILE_STATE_MAP[tileType](tilesState);

  // Handle single tile types (focusedVisualization, single)
  if (tileType === "focusedVisualization" || tileType === "single") {
    dispatch(action({ tile: newTileConfig }));
    return;
  }

  // Handle tile groups
  dispatch(
    action({
      ...currentTiles,
      [tileKey]: newTileConfig,
    }),
  );
};

export const toggleTileVisibility = (
  tileType: TileTypeKey,
  tileKey: string,
  hidden: boolean,
  tilesState: TilesState,
  dispatch: Dispatch,
) => {
  if (!isValidTileType(tileType)) {
    console.warn(`Invalid tile type: ${tileType}`);
    return;
  }

  const action = TILE_ACTION_MAP[tileType];
  const currentTiles = TILE_STATE_MAP[tileType](tilesState);

  // Handle single tile types
  if (tileType === "focusedVisualization" || tileType === "single") {
    const singleTile = currentTiles as
      | IFocusedVisualizationTile
      | ISingleViewTile;
    dispatch(
      action({
        ...singleTile,
        tile: {
          ...singleTile.tile,
          hidden,
        },
      }),
    );
    return;
  }

  // Handle tile groups
  const tileGroup = currentTiles as
    | ITileGroup
    | IVisualizationTileGroup
    | IFocusedTileGroup;
  dispatch(
    action({
      ...tileGroup,
      [tileKey]: {
        ...tileGroup[tileKey as keyof typeof tileGroup],
        hidden,
      },
    }),
  );
};

export const getTileTypeLabel = (tileType?: TileTypeKey | string): string => {
  switch (tileType) {
    case "hitting":
      return " - HITTING";
    case "pitching":
      return " - PITCHING";
    case "visualization":
    case "focusedVisualization":
      return " - VISUALIZATION";
    default:
      return "";
  }
};
